import {Box, Switch, Typography} from '@mui/material';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import React from 'react';
import {
  Column,
  createColumn,
  ExpandableTable
} from '../../../../components/ExpandableTable';
import {FeatureTemplate} from '../../../../model/license/feature-template.model';
import {useOrganizationUserLicensePermissionDrawerState} from '../state/organization-user-license-permission-drawer-state';

type Props = {
  userName: string | null
  userId: string
  organizationId: string
  isDrawerOpen: boolean
  onDrawerClosed: () => void
  getFeatureDetailsId: (featureTemplateId: string) => string | null

  mainTableRows: FeatureTemplate[]
  getChildRows: (row: FeatureTemplate) => FeatureTemplate[];
}

export const OrganizationUserLicensePermissionDrawer = ({mainTableRows, getChildRows, userName, userId, organizationId, getFeatureDetailsId, isDrawerOpen, onDrawerClosed}: Props) => {
  const state = useOrganizationUserLicensePermissionDrawerState({userId, organizationId})

  const columns: Column<FeatureTemplate>[] = [
    createColumn({ key: 'isActivated', width: 180, label: state.translateLocal('column.active'), render: (row) => {
      if (row.id.startsWith('module-header-')) {
        const children = getChildRows(row)
        const activeCount = children.filter(child => state.isPermissionChecked(child.id)).length
        return <Typography variant="body2">{state.translateLocal('activeCount', { active: activeCount, total: children.length })}</Typography>
      }
      return <Switch
        checked={state.isPermissionChecked(row.id)}
        onChange={async () => {
          await state.handleSwitchChange(getFeatureDetailsId(row.id), !state.isPermissionChecked(row.id))
        }}
        color="primary"
      />
    }}),
    createColumn({ key: 'featureModule', label: state.translateLocal('column.moduleOrFeature'), render: (row) =>
      row.id.startsWith('module-header-')
        ? state.translate(`licenseDetails.feature.module.${row.featureModule}`)
        : state.translate(`licenseDetails.feature.type.${row.featureType}`)
    }),
  ];

  const buildHeader = () =>     <Box mb={2}>
    <Typography variant="subtitle1" component="span">
      {state.translateLocal('title')}{' '}
    </Typography>
    {userName && (
      <Typography
        variant="subtitle1"
        component="span"
        fontWeight={600}
      >
        {userName}
      </Typography>
    )}
  </Box>

  return <Drawer
    anchor="right"
    open={isDrawerOpen}
    onClose={onDrawerClosed}
    PaperProps={{
      sx: {
        width: '75%',
        marginTop: '64px',
        height: `calc(100% - 64px)`,
        position: 'fixed',
        right: 0,
        padding: 3,
        overflowY: 'auto',
      }
    }}
  >
    {buildHeader()}
    <Stack spacing={3}>
      <ExpandableTable
        rows={mainTableRows}
        columns={columns}
        getChildRows={getChildRows}
        isRowOpen={(row) => state.isRowOpen(row.id)}
        onRowToggle={(row) => state.handleRowToggle(row.id)}
        rowKey={(row) => row.id}
      />
    </Stack>
  </Drawer>
}